import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch exchange rates from backend proxy
  const fetchExchangeRates = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${API_URL}/api/currency/rates?from=USD`
      );
      const result = await response.json();
      const data = result.rates || result;

      const ratesWithUSD = {
        USD: 1,
        ...data,
        AED: 3.67, // Pegged
        KZT: 535, // Manual approx rate
      };

      setRates(ratesWithUSD);

      // Cache rates with timestamp
      localStorage.setItem('exchangeRates', JSON.stringify(ratesWithUSD));
      localStorage.setItem('ratesTimestamp', Date.now().toString());

      return ratesWithUSD;
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      // Return fallback rates
      return {
        USD: 1,
        EUR: 0.92,
        GBP: 0.79,
        CAD: 1.35,
        AUD: 1.52,
        AUD: 1.52,
        JPY: 148.5,
        AED: 3.67,
        KZT: 535,
      };
    }
  };

  // Detect currency from IP geolocation
  const detectCurrencyFromIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      // Map of supported currencies
      const supportedCurrencies = ['USD', 'EUR', 'AED', 'KZT'];

      if (data.currency && supportedCurrencies.includes(data.currency)) {
        return data.currency;
      }

      return 'USD'; // Default fallback
    } catch (error) {
      console.error('Failed to detect currency from IP:', error);
      return 'USD';
    }
  };

  // Initialize currency and rates
  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        // Check for saved currency preference
        const savedCurrency = localStorage.getItem('selectedCurrency');

        // Check for cached rates
        const cachedRates = localStorage.getItem('exchangeRates');
        const ratesTimestamp = localStorage.getItem('ratesTimestamp');

        let currentRates = {};

        // Use cached rates if less than 1 hour old
        if (cachedRates && ratesTimestamp) {
          const age = Date.now() - parseInt(ratesTimestamp);
          if (age < CACHE_DURATION) {
            currentRates = JSON.parse(cachedRates);
            setRates(currentRates);
          }
        }

        // Fetch fresh rates if cache is expired or missing
        if (Object.keys(currentRates).length === 0) {
          currentRates = await fetchExchangeRates();
        }

        // Set currency (saved preference or auto-detect)
        if (savedCurrency) {
          setCurrency(savedCurrency);
        } else {
          const detectedCurrency = await detectCurrencyFromIP();
          setCurrency(detectedCurrency);
          localStorage.setItem('selectedCurrency', detectedCurrency);
        }
      } catch (error) {
        console.error('Currency initialization error:', error);
        setCurrency('USD');
      } finally {
        setLoading(false);
      }
    };

    initializeCurrency();
  }, []);

  // Update currency selection
  const updateCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('selectedCurrency', newCurrency);
  };

  // Convert price from USD to selected currency
  const convertPrice = (priceInUSD) => {
    if (!priceInUSD || isNaN(priceInUSD)) return 0;
    const rate = rates[currency] || 1;
    return priceInUSD * rate;
  };

  // Format price with currency symbol
  const formatPrice = (priceInUSD) => {
    const convertedPrice = convertPrice(priceInUSD);

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(convertedPrice);
    } catch (error) {
      // Fallback formatting
      const symbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        CAD: 'C$',
        AUD: 'A$',
        JPY: '¥',
        AED: 'AED ',
        KZT: '₸',
      };
      const symbol = symbols[currency] || '$';
      return `${symbol}${convertedPrice.toFixed(2)}`;
    }
  };

  const value = {
    currency,
    rates,
    loading,
    updateCurrency,
    convertPrice,
    formatPrice,
    refreshRates: fetchExchangeRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
